// Search-state for the capacities index route. The route in AppRouter.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers
// don't touch the router primitives directly.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { format, isValid, parseISO } from 'date-fns';

export interface CapacitiesSearchParams {
  // Calendar date the user is viewing capacities for. ISO `YYYY-MM-DD` when
  // present; empty string means "use the Rails-rendered prop" — the controller
  // already defaults to today in Asia/Singapore, so this avoids the client
  // clock and the server clock ever disagreeing on first paint.
  serving_date: string;
}

export const CAPACITIES_SEARCH_DEFAULTS: CapacitiesSearchParams = {
  serving_date: '',
};

const CAPACITIES_ROUTE = '/capacities' as const;

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// Strict YYYY-MM-DD validation: regex catches the shape, parseISO+isValid catches
// nonsense like `2026-13-01`, and the roundtrip via `format` catches calendar
// invalids like `2026-02-30` that parseISO silently rolls into the next month.
// Mirrors the parse→isValid pattern in parseClosedHoursQuery.ts.
function isValidIsoDate(value: string): boolean {
  if (!DATE_REGEX.test(value)) return false;
  const parsed = parseISO(value);
  if (!isValid(parsed)) return false;
  return format(parsed, 'yyyy-MM-dd') === value;
}

// validateSearch handler — coerces unknown URL input into the typed shape.
// Garbage or missing values fall back to '' so the consumer can defer to the
// SSR-equivalent prop, mirroring useEventsRouterSearch's empty-date pattern.
export function parseCapacitiesSearch(raw: Record<string, unknown>): CapacitiesSearchParams {
  const serving_date =
    typeof raw.serving_date === 'string' && isValidIsoDate(raw.serving_date)
      ? raw.serving_date
      : '';
  return { serving_date };
}

export function useCapacitiesRouterSearch() {
  const search = useSearch({ from: CAPACITIES_ROUTE });
  const tanNavigate = useNavigate({ from: CAPACITIES_ROUTE });

  const navigate = useCallback(
    (updates: Partial<CapacitiesSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setServingDate: (serving_date: string) => navigate({ serving_date }),
    }),
    [search, navigate],
  );
}
